import type { RequestBody } from '@vrplatform/data';

export type FeeReservationPreviewFilterLine = {
  status: 'active' | 'inactive';
  type?: string | null;
  amount: number;
  fee: number;
  account?:
    | ({
        id: string;
      } & Record<string, unknown>)
    | null;
} & Record<string, unknown>;

export type FeeReservationPreviewFilterResponse<
  TLine extends
    FeeReservationPreviewFilterLine = FeeReservationPreviewFilterLine,
> = {
  totals: {
    fee: number;
  } & Record<string, unknown>;
  lines: readonly TLine[];
};

type PreviewReservation = {
  bookingPlatform?: string | null;
  status?: string | null;
};

type AccountTotalCondition = NonNullable<
  RequestBody<'post:/recurring-fees/preview'>['accountTotalConditions']
>[number];

type PreviewFilters = {
  bookingChannelsFilter: 'all-channels' | string[] | null | undefined;
  statusFilter: 'booked' | 'canceled' | null | undefined;
  accountTotalConditions: RequestBody<'post:/recurring-fees/preview'>['accountTotalConditions'];
};

const normalizeString = (value: string | null | undefined) =>
  value?.trim().toLowerCase() ?? null;

const PREVIEW_ACCOUNT_TOTAL_ROLLUP_TYPES = new Set<string>([
  'reservation',
  'transaction_deposit',
  'transaction_expense',
  'transaction_transfer',
  'transaction_payout',
] as const);

export const normalizePreviewReservationStatus = (
  value: PreviewReservation['status'] | null | undefined
): 'booked' | 'canceled' | 'inactive' | null => {
  const normalized = normalizeString(value);

  if (normalized === 'booked') return 'booked';
  if (normalized === 'canceled' || normalized === 'cancelled')
    return 'canceled';
  if (normalized === 'inactive') return 'inactive';

  return null;
};

export const getPreviewAccountTotals = (
  lines: readonly FeeReservationPreviewFilterLine[]
) => {
  const totals = new Map<string, number>();

  for (const line of lines) {
    if (
      line.status !== 'active' ||
      !line.account?.id ||
      (line.type && PREVIEW_ACCOUNT_TOTAL_ROLLUP_TYPES.has(line.type))
    ) {
      continue;
    }

    totals.set(
      line.account.id,
      (totals.get(line.account.id) ?? 0) + line.amount
    );
  }

  return totals;
};

const matchesAccountTotalCondition = (
  centTotal: number,
  operator: AccountTotalCondition['operator'],
  centValue: AccountTotalCondition['centValue']
) => {
  switch (operator) {
    case '>':
      return centTotal > centValue;
    case '>=':
      return centTotal >= centValue;
    case '=':
      return centTotal === centValue;
    case '<':
      return centTotal < centValue;
    case '<=':
      return centTotal <= centValue;
  }
};

export const doesFeeReservationPreviewMatchFilters = ({
  preview,
  reservation,
  filters,
}: {
  preview: FeeReservationPreviewFilterResponse | null | undefined;
  reservation: PreviewReservation;
  filters: PreviewFilters;
}) => {
  const selectedChannels = Array.isArray(filters.bookingChannelsFilter)
    ? filters.bookingChannelsFilter
        .map((channel) => normalizeString(channel))
        .filter((channel): channel is string => Boolean(channel))
    : [];

  const reservationChannel = normalizeString(reservation.bookingPlatform);

  if (
    selectedChannels.length > 0 &&
    (!reservationChannel || !selectedChannels.includes(reservationChannel))
  ) {
    return false;
  }

  if (filters.statusFilter) {
    const reservationStatus = normalizePreviewReservationStatus(
      reservation.status
    );

    if (!reservationStatus || reservationStatus !== filters.statusFilter) {
      return false;
    }
  }

  if (filters.accountTotalConditions?.length) {
    if (!preview) return false;

    const accountTotals = getPreviewAccountTotals(preview.lines);
    const selectedAccountIds = [
      ...new Set(
        filters.accountTotalConditions.map((condition) => condition.accountId)
      ),
    ];
    const aggregateAccountTotal = selectedAccountIds.reduce(
      (sum, accountId) => sum + (accountTotals.get(accountId) ?? 0),
      0
    );
    const aggregateConditions = [
      ...new Map(
        filters.accountTotalConditions.map((condition) => [
          `${condition.operator}:${condition.centValue}`,
          {
            operator: condition.operator,
            centValue: condition.centValue,
          },
        ])
      ).values(),
    ];

    const hasMatchingTotals = aggregateConditions.every((condition) =>
      matchesAccountTotalCondition(
        aggregateAccountTotal,
        condition.operator,
        condition.centValue
      )
    );

    if (!hasMatchingTotals) {
      return false;
    }
  }

  return true;
};

export const getFeeReservationPreviewWithAppliedFilters = <
  TPreview extends FeeReservationPreviewFilterResponse | null | undefined,
>({
  preview,
  reservation,
  filters,
}: {
  preview: TPreview;
  reservation: PreviewReservation;
  filters: PreviewFilters;
}): TPreview => {
  if (!preview) return preview;

  if (
    doesFeeReservationPreviewMatchFilters({ preview, reservation, filters })
  ) {
    return preview;
  }

  return {
    ...preview,
    totals: {
      ...preview.totals,
      fee: 0,
    },
    lines: preview.lines.map((line) => ({
      ...line,
      fee: 0,
    })),
  } as TPreview;
};
